import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers,
  faHeart,
  faTasks,
  faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../context/AuthContext";
import { useNotification } from "../common/NotificationContext";
import { Button } from "@cakrawalajakarta/shared-components";
import { db } from "@lib/firebase";
import { collection, query, where, getDocs, limit } from "firebase/firestore";

function Volunteer() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [searchParams, setSearchParams] = useSearchParams();
  const [registrationStatus, setRegistrationStatus] = useState(null);
  const [notificationShown, setNotificationShown] = useState(false);

  const checkRegistration = async () => {
    if (!user) return;

    try {
      const q = query(
        collection(db, "pendaftaranVolunteers"),
        where("uid", "==", user.uid),
        limit(1)
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        setRegistrationStatus("registered");
        // Hanya tampilkan notifikasi jika query parameter registered=true dan belum ditampilkan
        if (searchParams.get("registered") === "true" && !notificationShown) {
          showNotification("info", "You are already registered as a volunteer.");
          setNotificationShown(true);
          setSearchParams({}, { replace: true });
        }
      } else {
        setRegistrationStatus("not_registered");
      }
    } catch (err) {
      showNotification("error", "Failed to check registration status.");
    }
  };

  useEffect(() => {
    if (!user) {
      navigate("/Login");
      showNotification(
        "error",
        "Please login to register as a volunteer."
      );
      return;
    }
    checkRegistration();
  }, [
    user,
    navigate,
    showNotification,
    searchParams,
    notificationShown,
    setSearchParams,
  ]);

  const handleVolunteerRegister = () => {
    navigate("/form-volunteer");
  };

  return (
    <section className="w-full min-h-screen text-lime-600 relative bg-white">
      <div
        className="relative w-full h-[60vh] flex items-center justify-center"
        style={{
          backgroundImage: 'url("/bg/volunteer.jpg")',
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-lime-600/100 to-lime-600/20 flex flex-col items-center justify-center gap-6">
          <motion.div
            initial={{ opacity: 0, y: -15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
          >
            <FontAwesomeIcon
              icon={faUsers}
              className="text-white text-5xl z-10"
              aria-label="Ikon Volunteer"
            />
          </motion.div>
            <h1 className="text-3xl md:text-5xl font-black uppercase text-white px-4 text-center">
              Volunteer Registration JOTR 2026
            </h1>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <motion.div
          className="space-y-8 max-w-6xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
        >
          <div className="text-center">
            <p className="text-lg leading-relaxed text-gray-700">
            <p className="text-lg leading-relaxed text-gray-700">
              Join as a volunteer to support the success of JOTR 2026! As a
              volunteer, you will be part of the team helping with the smooth
              running of the event in various sections, such as water station
              crew, racepack collection, marshalls, logistics, medical, and
              other criteria according to needs in the field. Registration is free!
            </p>
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col items-center justify-center space-y-2">
              <FontAwesomeIcon
                icon={faCalendarAlt}
                className="text-2xl text-lime-600"
              />
              <span className="text-sm font-black text-lime-600 uppercase tracking-wider">
                Event Date
              </span>
              <div className="text-sm text-gray-700">
                July 24 - 26, 2026
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col items-center justify-center space-y-2">
              <FontAwesomeIcon
                icon={faTasks}
                className="text-2xl text-lime-600"
              />
              <span className="text-sm font-black text-lime-600 uppercase tracking-wider">
                Volunteer Roles
              </span>
              <div className="text-sm text-gray-700 text-center">
                Contributing to support the smooth execution and success of the event.
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col items-center justify-center space-y-2">
              <FontAwesomeIcon
                icon={faHeart}
                className="text-2xl text-lime-600"
              />
              <span className="text-sm font-black text-lime-600 uppercase tracking-wider">
                Benefits
              </span>
              <div className="text-sm text-gray-700 text-center">
                Certificate, jersey/vest, and valuable experience.
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-lime-600 mb-4">
              Volunteer Requirements
            </h2>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>At least 17 years old.</li>
              <li>
                Strong teamwork spirit and dedication to helping the event.
              </li>
              <li>Physically fit and able to work in the field.</li>
              <li>
                Priority given to those with volunteer experience.
              </li>
            </ul>
          </div>

          <div className="mt-6">
            {registrationStatus === "registered" ? (
              <Button
                variant="primary"
                className="w-full opacity-80 cursor-default"
                disabled
              >
                YOU ARE ALREADY REGISTERED AS A VOLUNTEER
              </Button>
            ) : (
              <div className="space-y-4">
                <Button
                  onClick={handleVolunteerRegister}
                  variant="primary"
                  className="w-full"
                >
                  REGISTER NOW
                </Button>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default Volunteer;
